import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getComplianceItems,
  syncComplianceItems,
  updateComplianceItem,
  uploadComplianceDocument,
} from '../api/compliance';
import { ComplianceItem, ResourceType } from '../data/types';
import { useAuthState } from '../contexts/authcontext';
import {
  filterComplianceItemsForResource,
  tracksKitchenComplianceRequirements,
  ComplianceLocationFields,
  buildComplianceLocationSnapshot,
  complianceItemsNeedLocationRefresh,
} from '../utils/complianceHelpers';

const staleTime = 60 * 1000;

type UseLoadComplianceItemsArgs = {
  resourceId?: string;
  resourceType?: ResourceType;
  autoSync?: boolean;
  location?: ComplianceLocationFields;
};

/** Load kitchen compliance items scoped to resource id/type and location. */
export const useLoadComplianceItems = ({
  resourceId,
  resourceType = 'kitchen',
  autoSync = true,
  location,
}: UseLoadComplianceItemsArgs) => {
  const { communityId } = useAuthState();
  const queryClient = useQueryClient();
  const isKitchenCompliance = tracksKitchenComplianceRequirements(resourceType);
  const locationSnapshot = location ? buildComplianceLocationSnapshot(location) : '';

  const queryKey = [
    'complianceItems',
    communityId,
    resourceId,
    resourceType,
    locationSnapshot,
  ];

  const {
    data: items = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!communityId || !resourceId || !isKitchenCompliance) {
        return [];
      }

      const fetched = await getComplianceItems(communityId, {
        resourceId,
        resourceType,
      });
      const scopedItems = filterComplianceItemsForResource(
        fetched,
        resourceId,
        resourceType
      );

      const needsRefresh =
        autoSync &&
        location &&
        (scopedItems.length === 0 ||
          complianceItemsNeedLocationRefresh(scopedItems, location));

      if (needsRefresh) {
        const synced = await syncComplianceItems(communityId, {
          resourceId,
          resourceType,
          force: scopedItems.length > 0,
        });
        return filterComplianceItemsForResource(
          synced.items,
          resourceId,
          resourceType
        );
      }

      return scopedItems;
    },
    staleTime,
    enabled: Boolean(communityId && resourceId && isKitchenCompliance),
  });

  const syncMutation = useMutation({
    mutationFn: async (force?: boolean) => {
      if (!communityId || !resourceId || !isKitchenCompliance) {
        return { items: [] as ComplianceItem[] };
      }
      return syncComplianceItems(communityId, {
        resourceId,
        resourceType,
        force,
      });
    },
    onSuccess: (result) => {
      if (!resourceId) {
        return;
      }
      queryClient.setQueryData(
        queryKey,
        filterComplianceItemsForResource(result.items, resourceId, resourceType)
      );
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async ({ itemId, file }: { itemId: string; file: File }) => {
      if (!communityId) {
        return null;
      }
      return uploadComplianceDocument(communityId, itemId, file);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      itemId,
      updates,
    }: {
      itemId: string;
      updates: Partial<ComplianceItem>;
    }) => {
      if (!communityId) {
        return null;
      }
      return updateComplianceItem(communityId, itemId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    items,
    isLoading,
    isError,
    refetch,
    syncCompliance: syncMutation.mutateAsync,
    isSyncing: syncMutation.isPending,
    uploadComplianceDocument: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
    updateComplianceItem: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
};
